import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import defaultArt from '../assets/images/dj_kip_art.png';
import { useNavigate } from 'react-router-dom';
import {
  useMediaQuery,
  useTheme,
  Card,
  CardHeader,
  CardActionArea,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Avatar,
  Badge,
  Link,
  Box,
  Grid,
  Stack,
  IconButton,
  Chip,
  CardContent,
  ListItem,
} from '@mui/material';
import { useApi } from '../context/ApiProvider';
import { useSnackbar } from 'notistack';
import {
  Visibility,
  VisibilityOutlined,
  Star,
  StarBorder,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import ArtistsName from './ArtistsName';

const TrackItem = ({ item, asCard }) => {
  const { enqueueSnackbar } = useSnackbar();
  let navigate = useNavigate();
  const api = useApi();
  const {
    art_url_sm,
    artist,
    artists,
    title,
    id,
    user_fav,
    set_meta,
    release_info,
  } = item;
  const [isFav, setIsFav] = useState(user_fav?.is_fav);
  const [showLikes, setShowLikes] = useState(user_fav?.show_likes);

  // Use release_info for identified IDs, fallback to set_meta for unidentified
  const isIdentified = release_info?.is_identified;
  const displaySetMeta = isIdentified
    ? {
        set_name: release_info.set_name,
        id: release_info.set_id,
        permalink: release_info.set_permalink,
      }
    : set_meta;

  // Update title for identified IDs to show track position
  const displayTitle =
    isIdentified && release_info?.track_position
      ? `ID ${release_info.track_position}`
      : title;

  const dateFormatted = set_meta?.release_date
    ? new Date(set_meta.release_date).toLocaleDateString('en-US')
    : '';

  // Format release date for identified IDs
  const releaseDateFormatted =
    isIdentified && release_info?.release_date
      ? new Date(release_info.release_date).toLocaleDateString('en-US')
      : '';

  const trackTitle = `${displayTitle} in ${displaySetMeta?.set_name}`;

  const firstLikedDateFormatted = item.user_fav?.first_liked_date
    ? new Date(item.user_fav.first_liked_date).toLocaleDateString('en-US')
    : '';

  function goToTrack() {
    navigate(`/tracks/${id}`);
  }

  async function handleFavoriteClick() {
    const response = await api.put(`/favs/track/${item.id}`);
    if (response.ok) {
      setIsFav(response.body.is_fav);
      handleFavNotification(response.body.is_fav, 'favorites');
    }
  }

  function handleFavNotification(showAdded, type) {
    if (showAdded) {
      enqueueSnackbar(`ID added to ${type}!`);
    } else {
      enqueueSnackbar(`ID removed from ${type}.`);
    }
  }

  async function handleLikeClick() {
    const response = await api.put(`/favs/track/${item.id}/likes-toggle`);
    if (response.ok) {
      setShowLikes(response.body.show_likes);
      handleLikeNotification(response.body.show_likes, 'watching');
    }
  }

  function handleLikeNotification(showAdded, type) {
    if (showAdded) {
      enqueueSnackbar(`Started ${type} ID!`);
    } else {
      enqueueSnackbar(`Stopped ${type} ID.`);
    }
  }

  return (
    <>
      {asCard ? (
        <Card sx={{ overflow: 'visible' }}>
          <CardHeader
            action={
              <>
                <IconButton aria-label="favorite" onClick={handleFavoriteClick}>
                  {isFav ? <Star /> : <StarBorder />}
                </IconButton>
                <IconButton aria-label="favorite" onClick={handleLikeClick}>
                  {showLikes ? (
                    <Badge
                      color="primary"
                      badgeContent={user_fav?.user_likes}
                      max={999}
                    >
                      <Visibility />
                    </Badge>
                  ) : (
                    <VisibilityOutlined />
                  )}
                </IconButton>
              </>
            }
            title={dateFormatted}
            titleTypographyProps={{ fontSize: '1rem' }}
            sx={{ paddingBottom: 0 }}
          />
          <CardActionArea onClick={goToTrack}>
            <CardHeader
              avatar={<Avatar src={art_url_sm || defaultArt} />}
              title={
                <ArtistsName
                  artists={artists || []}
                  underline="hover"
                  linkColor="primary"
                />
              }
              subheader={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <span>{trackTitle}</span>
                </Stack>
              }
              sx={{
                '& .MuiCardHeader-content': {
                  overflow: 'hidden',
                },
                '& .MuiCardHeader-title': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
                '& .MuiCardHeader-subheader': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            />
          </CardActionArea>
          <CardContent sx={{ py: 0 }}>
            <Paper
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                flexWrap: 'nowrap',
                listStyle: 'none',
                p: 0,
                m: 0,
                backgroundColor: 'transparent',
                boxShadow: 'none',
                backgroundImage: 'none',
                overflow: 'hidden',
                maxWidth: '100%',
              }}
              component="ul"
            >
              {isIdentified && (
                <ListItem sx={{ width: 'unset', padding: '0 8px 8px 0' }}>
                  <Chip
                    label="Identified"
                    color="primary"
                    size="small"
                    sx={{
                      maxWidth: '120px',
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  />
                </ListItem>
              )}
              {firstLikedDateFormatted && (
                <ListItem sx={{ width: 'unset', padding: '0 8px 8px 0' }}>
                  <Chip
                    label={`First Liked: ${firstLikedDateFormatted}`}
                    color={isIdentified ? 'default' : 'secondary'}
                    size="small"
                    sx={{}}
                  />
                </ListItem>
              )}
            </Paper>
          </CardContent>
        </Card>
      ) : (
        <TableRow
          sx={{
            '.MuiTableCell-root': { padding: 1 },
          }}
        >
          <TableCell component="th" scope="row">
            <Avatar
              src={art_url_sm || defaultArt}
              sx={{ width: 56, height: 56 }}
            />
          </TableCell>
          <TableCell align="left">
            <Stack direction="row" alignItems="center" spacing={1}>
              <ArtistsName
                artists={artists || []}
                underline="hover"
                linkColor="primary"
              />
              <span>-</span>
              <Link
                href=""
                to={`/sets/${displaySetMeta?.id}`}
                underline="hover"
              >
                {displaySetMeta?.set_name}
              </Link>
            </Stack>
          </TableCell>
          <TableCell align="left">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Link
                href={''}
                to={`/tracks/${id}`}
                fontWeight="bold"
                underline="hover"
              >
                {displayTitle}
              </Link>
              {isIdentified && (
                <Chip
                  label="Identified"
                  size="small"
                  sx={{
                    backgroundColor: '#DEFF7B',
                    color: 'rgba(0, 0, 0, 0.87)',
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    height: '20px',
                  }}
                />
              )}
            </Stack>
          </TableCell>
          <TableCell align="left">
            {item.user_fav?.first_liked_date
              ? new Date(item.user_fav.first_liked_date).toLocaleDateString(
                  'en-US'
                )
              : ''}
          </TableCell>
          <TableCell align="left">{releaseDateFormatted}</TableCell>
          <TableCell align="right">
            <Stack direction="row" alignItems="center">
              <IconButton aria-label="favorite" onClick={handleLikeClick}>
                <Badge
                  color="primary"
                  badgeContent={user_fav?.user_likes}
                  max={999}
                >
                  {showLikes ? <Visibility /> : <VisibilityOutlined />}
                </Badge>
              </IconButton>
            </Stack>
          </TableCell>
          <TableCell align="center">
            <IconButton aria-label="favorite" onClick={handleFavoriteClick}>
              {isFav ? <Star /> : <StarBorder />}
            </IconButton>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

TrackItem.propTypes = {
  item: PropTypes.object,
  asCard: PropTypes.bool,
};

const TracksTable = ({ items, onTableInteraction, tableInteractionConfig }) => {
  const [directionAsc, setDirectionAsc] = useState(false);
  const [activeCol, setActiveCol] = useState('created_dt');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Sync internal state with tableInteractionConfig
  useEffect(() => {
    if (tableInteractionConfig?.order_by) {
      const orderBy = tableInteractionConfig.order_by;
      // Extract column name from order_by
      let column = '';
      if (orderBy.includes('user_track_favs.first_liked_date')) {
        column = 'first_liked_date';
      } else if (orderBy.includes('user_track_favs.total_user_likes')) {
        column = 'likes';
      } else if (orderBy.includes('user_track_favs.is_fav')) {
        column = 'favorites';
      } else if (orderBy.includes('track_meta.')) {
        column = orderBy.replace('track_meta.', '');
      }

      if (column) {
        setActiveCol(column);
        const isAsc = tableInteractionConfig.order_direction === 'asc';
        setDirectionAsc(isAsc);
      }
    }
  }, [tableInteractionConfig]);

  const handleTableHeadClick = (selectedColumn) => {
    console.log('Table head clicked:', selectedColumn); // Debug log
    let dir = directionAsc;
    if (selectedColumn === activeCol) {
      setDirectionAsc(!directionAsc);
      dir = !dir;
    } else {
      // For new column, check the current config direction or default to desc
      const currentDirection =
        tableInteractionConfig?.order_direction || 'desc';
      const newDirAsc = currentDirection === 'asc';
      setDirectionAsc(newDirAsc);
      dir = newDirAsc;
    }
    setActiveCol(selectedColumn);
    let orderBy =
      selectedColumn === 'first_liked_date'
        ? 'user_track_favs.first_liked_date'
        : selectedColumn === 'likes'
        ? 'user_track_favs.total_user_likes'
        : selectedColumn === 'favorites'
        ? 'user_track_favs.is_fav'
        : `track_meta.${selectedColumn}`;

    console.log('Order by:', orderBy, 'Direction:', dir ? 'asc' : 'desc'); // Debug log

    if (onTableInteraction) {
      onTableInteraction({
        ...tableInteractionConfig,
        order_by: orderBy,
        order_direction: dir ? 'asc' : 'desc',
      });
    } else {
      console.error('onTableInteraction function is not provided');
    }
  };

  if (!items.length) return <></>;

  return (
    <>
      {isMobile ? (
        <Box sx={{ flexGrow: 1, textAlign: 'left' }} my={3}>
          <Grid container spacing={2}>
            {items.map((item) => (
              <Grid item xs={12} lg={3} key={item.id}>
                <TrackItem item={item} asCard />
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ overflow: 'visible' }}
        >
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow
                sx={{
                  '.MuiTableCell-root': { padding: '16px 8px' },
                }}
              >
                <TableCell
                  sx={{
                    width: '5%',
                  }}
                ></TableCell>
                <TableCell
                  align="left"
                  sx={{
                    fontWeight: activeCol === 'set_name' ? 'bold' : 'normal',
                    cursor: 'pointer',
                    width: '30%',
                  }}
                  onClick={() => handleTableHeadClick('set_name')}
                >
                  Set
                  {activeCol === 'set_name' && (
                    <>
                      {directionAsc ? (
                        <ArrowUpward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      ) : (
                        <ArrowDownward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    fontWeight: activeCol === 'title' ? 'bold' : 'normal',
                    cursor: 'pointer',
                    width: '20%',
                  }}
                  onClick={() => handleTableHeadClick('title')}
                >
                  Title
                  {activeCol === 'title' && (
                    <>
                      {directionAsc ? (
                        <ArrowUpward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      ) : (
                        <ArrowDownward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    fontWeight:
                      activeCol === 'first_liked_date' ? 'bold' : 'normal',
                    cursor: 'pointer',
                    width: '10%',
                  }}
                  onClick={() => handleTableHeadClick('first_liked_date')}
                >
                  First Liked
                  {activeCol === 'first_liked_date' && (
                    <>
                      {directionAsc ? (
                        <ArrowUpward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      ) : (
                        <ArrowDownward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    fontWeight:
                      activeCol === 'release_date' ? 'bold' : 'normal',
                    cursor: 'pointer',
                    width: '10%',
                  }}
                  onClick={() => handleTableHeadClick('release_date')}
                >
                  Release Date
                  {activeCol === 'release_date' && (
                    <>
                      {directionAsc ? (
                        <ArrowUpward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      ) : (
                        <ArrowDownward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    fontWeight: activeCol === 'likes' ? 'bold' : 'normal',
                    cursor: 'pointer',
                    width: '10%',
                  }}
                  onClick={() => handleTableHeadClick('likes')}
                >
                  Watched
                  {activeCol === 'likes' && (
                    <>
                      {directionAsc ? (
                        <ArrowUpward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      ) : (
                        <ArrowDownward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    fontWeight: activeCol === 'favorites' ? 'bold' : 'normal',
                    cursor: 'pointer',
                    width: '10%',
                  }}
                  onClick={() => handleTableHeadClick('favorites')}
                >
                  Favorites
                  {activeCol === 'favorites' && (
                    <>
                      {directionAsc ? (
                        <ArrowUpward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      ) : (
                        <ArrowDownward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      )}
                    </>
                  )}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TrackItem item={item} key={item.id} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

TracksTable.propTypes = {
  items: PropTypes.array,
  onTableInteraction: PropTypes.func,
  tableInteractionConfig: PropTypes.object,
};

export default TracksTable;
